//#region Util Imports

import * as moment from 'moment'
import * as _ from 'underscore'

//#endregion

//#region DevExtreme Imports

import DevExpress from 'devextreme'
import ArrayStore from 'devextreme/data/array_store'
import DataSource from 'devextreme/data/data_source'

//#endregion

import { KeyValuePair } from '../../api/api.models'
import { EventsNames } from '../../../helpers/events-names'
import { CustomDatePeriod, DatePickerBridge } from '../date-picker/date-picker.directive'
import { TemplateHelper, ConfigHelper, Dictionary, LocalizationHelper } from '../../../helpers'
import { OperatorLayoutSetting } from '../../../models/local'
import { Enums } from '../../../models/common'
import { ActionSelectedFilters, IIMRActionFilters, IMRActionFiltersOptions } from './action-filters.interfaces'

//#region Asset Imports

import { IMRBaseControlController } from '../base'
import { UserSessionService } from '../../../services/user-session'
import { IReferenceType } from 'app/models/util/interfaces'
import { DictionaryKeyType } from 'app/helpers/dictionary'
import { SelectPopoverOptions } from '../select-popover/select-popover.directive'

//#endregion

export class ActionFiltersController
    extends
    IMRBaseControlController<IIMRActionFilters, IMRActionFiltersOptions>
    implements
    IIMRActionFilters {

    constructor(
        private $scope: ng.IScope,
        private userSession: UserSessionService
    ) {
        super()
    }

    public cancelButtonDisabled: boolean
    public selectedFilters: ActionSelectedFilters
    public refreshButtonOptions: DevExpress.ui.dxButtonOptions
    public cancelButtonOptions: DevExpress.ui.dxButtonOptions
    public settingsButtonOptions: DevExpress.ui.dxButtonOptions
    public settingsPopoverOptions: DevExpress.ui.dxPopoverOptions
    public datePickerBridge: DatePickerBridge
    public deviceNumberBoxOptions: DevExpress.ui.dxNumberBoxOptions
    public typeSelectBoxOptions: DevExpress.ui.dxSelectBoxOptions
    public stateSelectBoxOptions: DevExpress.ui.dxSelectBoxOptions
    public moduleSelectBoxOptions: DevExpress.ui.dxSelectBoxOptions
    public operatorSelectPopoverOptions: SelectPopoverOptions
    public locationSelectPopoverOptions: SelectPopoverOptions
    public meterSelectPopoverOptions: SelectPopoverOptions

    public onLocationSelected: (locationId: number, dataType, locationName: string) => void
    public onMeterSelected: (meterId: number, dataType, meterName: string) => void
    public onOperatorSelected: (operatorId: number, dataType, operatorName: string) => void

    private settingsPopover: DevExpress.ui.dxPopover
    private referenceTables: { [key: number]: any } = {}
    private typeSelectBox: DevExpress.ui.dxSelectBox
    private stateSelectBox: DevExpress.ui.dxSelectBox
    private moduleSelectBox: DevExpress.ui.dxSelectBox
    private deviceNumberBox: DevExpress.ui.dxNumberBox

    //#region Consts

    public get TYPE_FIELD_NAME(): string { return LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Type) }
    public get STATE_FIELD_NAME(): string { return LocalizationHelper.getLocalizedText(LocalizationHelper.Text.State) }
    public get OPERATOR_FIELD_NAME(): string { return LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Operator) }
    public get DEVICE_FIELD_NAME(): string { return LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Device) }
    public get LOCATION_FIELD_NAME(): string { return LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Location) }
    public get METER_FIELD_NAME(): string { return LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Meter) }
    public get MODULE_FIELD_NAME(): string { return LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Module) }

    public get ID_OPERATOR_DATA_TYPE(): number { return Enums.DataType.HELPER_ID_OPERATOR }
    public get ID_LOCATION_DATA_TYPE(): number { return Enums.DataType.HELPER_ID_LOCATION }
    public get ID_METER_DATA_TYPE(): number { return Enums.DataType.HELPER_ID_METER }

    //#endregion

    //#region Component

    protected initialize(options: IMRActionFiltersOptions): void {
        this.cancelButtonDisabled = true

        this.initializeFilters()
        this.initializeEvents()
        this.initializeWidgets()
    }

    //#endregion

    //#region IIMRActionFilters implementation

    public updateFilters(selectedFilters: ActionSelectedFilters): void {
        this.selectedFilters = selectedFilters

        this.resetDate(selectedFilters.selectedFromDate, selectedFilters.selectedToDate)

        if (selectedFilters.selectedType == null) {
            this.resetValue(this.typeSelectBox)
        }

        if (selectedFilters.selectedState == null) {
            this.resetValue(this.stateSelectBox)
        }

        if (selectedFilters.selectedLocation == null) {
            this.selectedFilters.selectedLocation = null
        }

        if (selectedFilters.selectedDevice == null) {
            this.resetValue(this.deviceNumberBox)
        }

        if (selectedFilters.selectedMeter == null) {
            this.selectedFilters.selectedMeter = null
        }

        if (selectedFilters.selectedOperator == null) {
            this.selectedFilters.selectedOperator = null
        }

        if (selectedFilters.selectedModule == null) {
            this.resetValue(this.moduleSelectBox)
        }
    }

    public filtersClearedRedirect(): void {
        this.selectedFilters = <ActionSelectedFilters>{}

        this.resetDate()
        this.resetValue(this.typeSelectBox)
        this.resetValue(this.stateSelectBox)
        this.resetValue(this.deviceNumberBox)
        this.resetValue(this.moduleSelectBox)
        this.selectedFilters.selectedOperator = null
        this.selectedFilters.selectedLocation = null
        this.selectedFilters.selectedMeter = null

        this.userSession.setOperatorLayoutSetting(new OperatorLayoutSetting(Enums.SimaxOperatorLayoutSetting.ActionLocationId), null)
        this.userSession.setOperatorLayoutSetting(new OperatorLayoutSetting(Enums.SimaxOperatorLayoutSetting.ActionMeterId), null)
        this.userSession.setOperatorLayoutSetting(new OperatorLayoutSetting(Enums.SimaxOperatorLayoutSetting.ActionOperator), null)
    }

    public disableCancelbutton(): void {
        this.cancelButtonDisabled = true
    }
    //#endregion

    private initializeEvents(): void {
        this.onLocationSelected = (locationId: number, dataType: number, locationName: string) => {
            this.selectedFilters.selectedLocation = (locationId > 0) ? { id: locationId, caption: locationName } : null
            this.userSession.setOperatorLayoutSetting(new OperatorLayoutSetting(Enums.SimaxOperatorLayoutSetting.ActionLocationId), this.selectedFilters.selectedLocation)
        }

        this.onMeterSelected = (meterId: number, dataType: number, meterName: string) => {
            this.selectedFilters.selectedMeter = (meterId > 0) ? { id: meterId, caption: meterName } : null
            this.userSession.setOperatorLayoutSetting(new OperatorLayoutSetting(Enums.SimaxOperatorLayoutSetting.ActionMeterId), this.selectedFilters.selectedMeter)
        }

        this.onOperatorSelected = (operatorId: number, dataType: number, operatorName: string) => {
            this.selectedFilters.selectedOperator = (operatorId > 0) ? { id: operatorId, caption: operatorName } : null
            this.userSession.setOperatorLayoutSetting(new OperatorLayoutSetting(Enums.SimaxOperatorLayoutSetting.ActionOperator), this.selectedFilters.selectedOperator)
        }
    }

    private resetValue(inputElement: DevExpress.ui.Editor): void {
        if (inputElement != null) {
            inputElement.option('value', null)
        }
    }

    private resetDate(fDate?: string, tDate?: string): void { //TODO refactor
        const dateFormat: string = window['simax'].getDateFormat('DATE_TIME_FORMAT_MOMENTJS')

        let date: Date = new Date()
        date.setHours(0, 0, 0, 0)

        const fD: moment.Moment = moment(date.setDate(new Date().getDate() - 6))
        this.selectedFilters.selectedFromDate = (fDate != null) ? fDate : fD.format(dateFormat)

        const fromDate: Date = fD.toDate()
        date = new Date()

        date.setHours(23, 59, 59, 999)
        const tD: moment.Moment = moment(date)
        this.selectedFilters.selectedToDate = tDate ? tDate : tD.format(dateFormat)

        const toDate: Date = tD.toDate()

        this.$scope.$broadcast(EventsNames.CHANGE_FROM_TO_DATE, fDate ? moment(fDate, dateFormat).format('YYYY-MM-DD HH:mm') : fromDate, tDate ? moment(tDate, dateFormat).format('YYYY-MM-DD HH:mm') : toDate) //TODO. replace event on method from date-picker in future
    }

    private initializeFilters(): void {
        this.selectedFilters = <ActionSelectedFilters>{}

        this.selectedFilters.selectedType = this.userSession.getOperatorLayoutSetting(new OperatorLayoutSetting(Enums.SimaxOperatorLayoutSetting.ActionType), null)
        this.selectedFilters.selectedState = this.userSession.getOperatorLayoutSetting(new OperatorLayoutSetting(Enums.SimaxOperatorLayoutSetting.ActionState), null)
        this.selectedFilters.selectedOperator = this.userSession.getOperatorLayoutSetting(new OperatorLayoutSetting(Enums.SimaxOperatorLayoutSetting.ActionOperator), null)
        this.selectedFilters.selectedDevice = this.userSession.getOperatorLayoutSetting(new OperatorLayoutSetting(Enums.SimaxOperatorLayoutSetting.ActionDeviceId), null)
        this.selectedFilters.selectedLocation = this.userSession.getOperatorLayoutSetting(new OperatorLayoutSetting(Enums.SimaxOperatorLayoutSetting.ActionLocationId), null)
        this.selectedFilters.selectedMeter = this.userSession.getOperatorLayoutSetting(new OperatorLayoutSetting(Enums.SimaxOperatorLayoutSetting.ActionMeterId), null)
        this.selectedFilters.selectedModule = this.userSession.getOperatorLayoutSetting(new OperatorLayoutSetting(Enums.SimaxOperatorLayoutSetting.ActionModule), null)

        //TODO refactor
        const dateFormat: string = window['simax'].getDateFormat('DATE_TIME_FORMAT_MOMENTJS')

        const fromDate: string = this.userSession.getOperatorLayoutSetting(new OperatorLayoutSetting(Enums.SimaxOperatorLayoutSetting.ActionFromDate), null)

        if (fromDate == null) {
            this.selectedFilters.selectedFromDate = moment(new Date().setDate(new Date().getDate() - 6)).format(dateFormat)
        } else {
            this.selectedFilters.selectedFromDate = moment(fromDate, dateFormat).format(dateFormat)
        }

        const toDate: string = this.userSession.getOperatorLayoutSetting(new OperatorLayoutSetting(Enums.SimaxOperatorLayoutSetting.ActionToDate), null)

        if (toDate == null) {
            this.selectedFilters.selectedToDate = moment().format(dateFormat)
        } else {
            this.selectedFilters.selectedToDate = moment(toDate, dateFormat).format(dateFormat)
        }

        setTimeout(() => {
            this.$scope.$broadcast(EventsNames.CHANGE_FROM_TO_DATE, moment(this.selectedFilters.selectedFromDate, dateFormat).format('YYYY-MM-DD HH:mm'), moment(this.selectedFilters.selectedToDate, dateFormat).format('YYYY-MM-DD HH:mm')) //TODO. replace event on method from date-picker in future
        })
    }


    private initializeWidgets(): void {
        this.operatorSelectPopoverOptions = {
            referenceTypeId: Enums.ReferenceType.IdOperator
        }

        this.locationSelectPopoverOptions = {
            referenceTypeId: Enums.ReferenceType.IdLocation
        }

        this.meterSelectPopoverOptions = {
            referenceTypeId: Enums.ReferenceType.IdMeter
        }

        this.refreshButtonOptions = {
            template: TemplateHelper.getIcon(ConfigHelper.Icon.RENEW_BUTTON, this.userSession),
            onClick: () => {
                if (_.isFunction(this.options.onFiltersUpdated)) {
                    this.options.onFiltersUpdated(this.selectedFilters)
                }

                this.userSession.setOperatorLayoutSetting(new OperatorLayoutSetting(Enums.SimaxOperatorLayoutSetting.ActionDeviceId), this.selectedFilters.selectedDevice)
                this.userSession.setOperatorLayoutSetting(new OperatorLayoutSetting(Enums.SimaxOperatorLayoutSetting.ActionType), this.selectedFilters.selectedType)
                this.userSession.setOperatorLayoutSetting(new OperatorLayoutSetting(Enums.SimaxOperatorLayoutSetting.ActionState), this.selectedFilters.selectedState)
                this.userSession.setOperatorLayoutSetting(new OperatorLayoutSetting(Enums.SimaxOperatorLayoutSetting.ActionModule), this.selectedFilters.selectedModule)

                this.cancelButtonDisabled = false
            },
            hint: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Refresh),
            height: 36
        }

        this.cancelButtonOptions = {
            template: TemplateHelper.getIcon(ConfigHelper.Icon.CLOSE, this.userSession),
            onClick: () => {
                if (_.isFunction(this.options.onFiltersUpdateCancel)) {
                    this.options.onFiltersUpdateCancel()
                }

                this.cancelButtonDisabled = true
            },
            bindingOptions: {
                disabled: '$ctrl.cancelButtonDisabled'
            },
            hint: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Cancel),
            height: 36
        }

        this.settingsButtonOptions = {
            template: TemplateHelper.getIcon(ConfigHelper.Icon.SETTINGS, this.userSession),
            hint: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.MoreFilters),
            onClick: () => {
                this.settingsPopover.toggle(true)
            },
            height: 36
        }

        this.settingsPopoverOptions = {
            width: 400,
            target: '.more-options-button',
            contentTemplate: 'settingsContent',
            onInitialized: (e: any) => {
                this.settingsPopover = e.component
            }
        }

        this.datePickerBridge = {
            fromDate: null,
            toDate: null,
            callback: (selectedPeriod: CustomDatePeriod) => {
                //TODO refactor
                if (selectedPeriod.id == "latest") {
                    this.datePickerBridge.fromDate = new Date()
                    this.datePickerBridge.fromDate.setMonth(this.datePickerBridge.toDate.getMonth() - 1)
                    this.datePickerBridge.fromDate.setHours(0, 0, 0, 0)
                    this.datePickerBridge.toDate = new Date()
                    this.datePickerBridge.toDate.setHours(23, 59, 59, 999)
                }
                const dateFormat: string = window['simax'].getDateFormat('DATE_TIME_FORMAT_MOMENTJS')
                this.selectedFilters.selectedFromDate = moment(this.datePickerBridge.fromDate).format(dateFormat)
                this.selectedFilters.selectedToDate = moment(this.datePickerBridge.toDate).format(dateFormat)
            },
            customPeriods: [
                {
                    id: "latest",
                    title: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Latest)
                }
            ],
            isSmallScreen: false
        }

        this.deviceNumberBoxOptions = {
            placeholder: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Select),
            height: 36,
            width: '100%',
            min: 0,
            showSpinButtons: true,
            showClearButton: true,
            value: this.selectedFilters.selectedDevice,
            onValueChanged: (e: any) => {
                this.selectedFilters.selectedDevice = e.value
            },
            onInitialized: (e: any) => {
                this.deviceNumberBox = e.component
            }
        }

        this.loadReferenceTables()
            .then(() => {
                this.typeSelectBoxOptions = {
                    placeholder: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Select),
                    dataSource: new DataSource({
                        store: new ArrayStore(this.referenceTables[Enums.ReferenceType.ActionType]),
                        paginate: true
                    }),
                    onInitialized: (e: any) => {
                        this.typeSelectBox = e.component
                    },
                    onValueChanged: (e: any) => {
                        this.selectedFilters.selectedType = null

                        if (e.value != null) {
                            const item: IReferenceType = _.find(this.referenceTables[Enums.ReferenceType.ActionType], (table: IReferenceType) => {
                                return table.getReferenceKey() == e.value
                            })

                            if (item != null) {
                                this.selectedFilters.selectedType = { id: item.getReferenceKey(), caption: item.toString() }
                            }
                        }
                    },
                    value: this.selectedFilters.selectedType != null ? this.selectedFilters.selectedType.id : undefined,
                    height: 36,
                    width: '100%',
                    valueExpr: (item: IReferenceType): number => {
                        if (item == null) {
                            return
                        }

                        return item.getReferenceKey()
                    },
                    displayExpr: (item: IReferenceType): string => {
                        if (item == null) {
                            return
                        }

                        return item.toString()
                    },
                    noDataText: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.NoData),
                    searchEnabled: true,
                    showClearButton: true
                }

                this.stateSelectBoxOptions = {
                    placeholder: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Select),
                    dataSource: new DataSource({
                        store: new ArrayStore(this.referenceTables[Enums.ReferenceType.IdActionStatus]),
                        paginate: true
                    }),
                    onInitialized: (e: any) => {
                        this.stateSelectBox = e.component
                    },
                    onValueChanged: (e: any) => {
                        this.selectedFilters.selectedState = null

                        if (e.value != null) {
                            const item: IReferenceType = _.find(this.referenceTables[Enums.ReferenceType.IdActionStatus], (table: IReferenceType) => {
                                return table.getReferenceKey() == e.value
                            })

                            if (item != null) {
                                this.selectedFilters.selectedState = { id: item.getReferenceKey(), caption: item.toString() }
                            }
                        }
                    },
                    value: this.selectedFilters.selectedState != null ? this.selectedFilters.selectedState.id : undefined,
                    height: 36,
                    width: '100%',
                    valueExpr: (item: IReferenceType) => {
                        if (item == null) {
                            return
                        }

                        return item.getReferenceKey()
                    },
                    displayExpr: (item: IReferenceType) => {
                        if (item == null) {
                            return
                        }

                        return item.toString()
                    },
                    noDataText: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.NoData),
                    searchEnabled: true,
                    showClearButton: true
                }

                this.moduleSelectBoxOptions = {
                    placeholder: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Select),
                    dataSource: new DataSource({
                        store: new ArrayStore(this.referenceTables[Enums.ReferenceType.IdModule]),
                        paginate: true
                    }),
                    onInitialized: (e: any) => {
                        this.moduleSelectBox = e.component
                    },
                    onValueChanged: (e: any) => {
                        this.selectedFilters.selectedModule = null

                        if (e.value != null) {
                            const item: IReferenceType = _.find(this.referenceTables[Enums.ReferenceType.IdActionStatus], (table: IReferenceType) => {
                                return table.getReferenceKey() == e.value
                            })

                            if (item != null) {
                                this.selectedFilters.selectedModule = { id: item.getReferenceKey(), caption: item.toString() }
                            }
                        }
                    },
                    value: this.selectedFilters.selectedModule ? this.selectedFilters.selectedModule.id : undefined,
                    height: 36,
                    width: '100%',
                    valueExpr: (item: IReferenceType) => {
                        if (item == null) {
                            return
                        }

                        return item.getReferenceKey()
                    },
                    displayExpr: (item: IReferenceType) => {
                        if (item == null) {
                            return
                        }

                        return item.toString()
                    },
                    noDataText: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.NoData),
                    searchEnabled: true,
                    showClearButton: true
                }
            })
    }

    private loadReferenceTables(): JQueryPromise<{ [key: number]: any }> {
        return this.userSession
            .getDataProvider().System
            .getDictionaries([Enums.ReferenceType.ActionType, Enums.ReferenceType.IdActionStatus, Enums.ReferenceType.IdModule, Enums.ReferenceType.IdOperator], true)
            .then((references: Dictionary<Dictionary<IReferenceType>>) => {

                if (references == null) {
                    return
                }

                const keys: DictionaryKeyType[] = references.keys()

                if (_.size(keys) === 0) {
                    return
                }

                for (let index in keys) {
                    const key = keys[index]

                    if (key != null) {
                        const refDictionary: Dictionary<IReferenceType> = references.tryGetValue(key)

                        let values: IReferenceType[] = []

                        if (refDictionary != null && refDictionary.length > 0) {
                            values = references.tryGetValue(key).toArray().map((item: KeyValuePair<DictionaryKeyType, IReferenceType>) => item.value)
                        }

                        this.referenceTables[key] = values
                    }
                }

                return this.referenceTables
            })
    }
}